/* eslint-disable react/style-prop-object */
import React, { useContext, useEffect, useState } from "react";
import { Input } from "../Helpers/Input/Input";
import Button from "../Helpers/Button/Button";
import axios from "axios";
import qs from "qs";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { webSocketManager } from "../../Sockets/WebSocketManager";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setUserAuth, setUserInf } from "../../Redux/service/Users/UsersSlice";
import { Helmet } from "react-helmet";
import trans, { isRTL } from "../../utils";
import { languagecontext } from "../../context/LanguageContext";
import { filtrationcontext } from "../../context/Filtercontext";
import { useLazyGetUserAuthQuery } from "../../Redux/service/Roles&Permissions/RolesPermissions";
import AES from "crypto-js/aes";
import useIsMobile from "../../hooks/useIsMobile";
import MobileLogin from "./MobileLogin";

const LOGIN_LOCKOUT_KEY = "login_lockout_until";

function getLockoutEndFromStorage() {
  const raw = localStorage.getItem(LOGIN_LOCKOUT_KEY);
  if (!raw) return null;
  const ts = parseInt(raw, 10);
  if (Number.isNaN(ts) || ts <= Date.now()) return null;
  return ts;
}

function parseLockoutMinutes(message) {
  if (!message || typeof message !== "string") return 5;
  const match = message.match(/try again in (\d+)\s*minute/i);
  return match ? Math.max(1, parseInt(match[1], 10)) : 5;
}

export default function AuthLogin() {
  const navigate = useNavigate();
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [lockoutEnd, setLockoutEnd] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  // Restore lockout from localStorage on mount / reload
  useEffect(() => {
    const end = getLockoutEndFromStorage();
    if (end) {
      setLockoutEnd(end);
      setSecondsLeft(Math.max(0, Math.ceil((end - Date.now()) / 1000)));
    }
  }, []);

  // Countdown timer and clear when expired
  useEffect(() => {
    if (!lockoutEnd || lockoutEnd <= Date.now()) return;
    const tick = () => {
      const left = Math.max(0, Math.ceil((lockoutEnd - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        localStorage.removeItem(LOGIN_LOCKOUT_KEY);
        setLockoutEnd(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockoutEnd]);
  const [fetchUserAuth, { data: userAuth, isLoading }] =
    useLazyGetUserAuthQuery();

  const Login = async (values) => {
    document.cookie
      .split(";")
      .forEach(
        (c) =>
          (document.cookie = c
            .replace(/^ +/, "")
            .replace(
              /=.*/,
              "=;expires=" + new Date().toUTCString() + ";path=/",
            )),
      );

    const auth = qs.stringify(values);
    try {
      // Step 1: Login using /session endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_AUTH_APIS_URL}/users/login`,
        auth,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `ApiKey ${process.env.REACT_APP_API_KEY}`,
          },
          withCredentials: true,
        },
      );

      dispatch(setUserInf(response.data));
      // Step 2: Get token using Basic Auth header only (no body)

      const userAuthresponse = await fetchUserAuth().unwrap();
      if (userAuthresponse?.data?.permissions?.length === 0) {
        await axios.delete(
          `${process.env.REACT_APP_AUTH_APIS_URL}/users/logout`,
          { withCredentials: true },
        );
        localStorage.removeItem("persist:root");
        navigate("/signin");
        toast.error(
          trans("Please contact your adminstrator to compelete your setup"),
          {
            autoClose: 1000,
            rtl: localStorage.getItem("lang") === "ar" ? true : false,
          },
        );
        return;
      } else {
        dispatch(setUserAuth(userAuthresponse?.data));
        const permissions = userAuthresponse?.data?.permissions ?? [];
        const has = (key) => permissions.some((p) => p.moduleKey === key);

        const target = has("DASHBOARD")
          ? "/dashboard"
          : has("DASHBOARDQAQC")
            ? "/dashboard/qa"
            : has("DASHBOARDFS")
              ? "/dashboard/fsm"
              : has("CONSULTANTDASHBOARD")
                ? "/dashboard/consultant"
                : "/setup/account";
        navigate(target);
      }

      webSocketManager.connect();
    } catch (error) {
      if (error.response?.status === 429) {
        const msg =
          error.response?.data?.message ||
          trans("Too many login attempts. Please try again in 5 minutes.");
        const minutes = parseLockoutMinutes(msg);
        const until = Date.now() + minutes * 60 * 1000;
        localStorage.setItem(LOGIN_LOCKOUT_KEY, String(until));
        setLockoutEnd(until);
        setSecondsLeft(minutes * 60);
        toast.error(msg, {
          autoClose: 8000,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      } else if (error.response?.status === 401) {
        toast.error(trans("Invalid Email or Password"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      } else if (error.response?.status === 500) {
        toast.info(trans("Internal Server Error"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      } else {
        toast.error(trans("Failed to login"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      }
    }
  };

  const validSchema = Yup.object({
    email: Yup.string()
      .required(trans("Email is required"))
      .email("Invalid email format"),
    password: Yup.string().required(trans("Password is required")),
  });

  const register = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validSchema,
    onSubmit: (values) => {
      Login(values);
    },
  });

  const handleSubmit = (e) => {
    if (lockoutEnd && lockoutEnd > Date.now()) return;
    setSubmitAttempt(true);
    register.handleSubmit(e);
  };

  const isLockedOut = lockoutEnd != null && secondsLeft > 0;
  const lockoutDisplay =
    secondsLeft > 0
      ? `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`
      : "";

  const lang = localStorage.getItem("lang") || "en";
  let { setLang } = useContext(languagecontext);
  let { setLoader } = useContext(filtrationcontext);

  const switchLanguage = () => {
    setLoader(true);
    window.location.reload();
    const currentLang = localStorage.getItem("lang");
    const newLang = currentLang === "ar" ? "en" : "ar";
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    document.body.dir = newLang === "ar" ? "rtl" : "ltr";
  };
  return (
    <>
      <Helmet>
        <title>Login</title>
        <meta name="description" content="Login" />
      </Helmet>
      {isMobile ? (
        <MobileLogin
          register={register}
          handleSubmit={handleSubmit}
          isLockedOut={isLockedOut}
          lockoutDisplay={lockoutDisplay}
          switchLanguage={switchLanguage}
          lang={lang}
          submitAttempt={submitAttempt}
        />
      ) : (
        <div className="welcome-layout " dir={lang === "ar" ? "rtl" : "ltr"}>
          <div className="container-fluid">
            <div className="row justify-content-between">
              <div className="col-lg-8 col-md-6">
                <div className="welcome-world">
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/New/UMA/Worlddd.png`}
                    alt="world"
                    className="contient"
                  />
                </div>
                <div className="our-services">
                  <div className={`tooltip-wrapper ${isRTL ? "ms-5" : "me-5"}`}>
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/New/UMA/Tracking.svg`}
                      alt="tracking"
                      width={60}
                    />
                    <span className="tooltip-text">Tracking</span>
                  </div>

                  <div className="tooltip-wrapper">
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/New/UMA/FSM.svg`}
                      alt="tracking"
                      width={60}
                    />
                    <span className="tooltip-text">FSM</span>
                  </div>
                </div>
                <h1 className="our-slogan">uma</h1>
              </div>

              <div
                className="col-lg-3 col-md-6"
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div>
                  <div
                    className="d-flex align-items-center justify-content-center mb-5"
                    dir="ltr"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/New/UMA/Lg-Logo.svg`}
                      alt="logo"
                    />
                    <div
                      style={{
                        color: "white",
                        marginInline: "8px",
                        fontSize: "36px",
                        fontWeight: "700",
                        textTransform: "capitalize",
                        zIndex: 5,
                      }}
                    >
                      soft sprint
                    </div>
                  </div>
                  <form
                    style={{
                      backgroundColor: "#005430",
                      paddingInline: "40px",
                      paddingBlock: "20px",
                      border: "3px solid white",
                      borderRadius: "16px",
                    }}
                    onSubmit={handleSubmit}
                  >
                    {/* Email Field */}
                    <div className="Auth-Form-vetrack">
                      {isLockedOut && (
                        <div
                          className="text-center mb-3 p-2"
                          style={{
                            backgroundColor: "#ffffff",
                            color: "#dc2626",
                            borderRadius: "8px",
                            border: "1px solid #dc2626",
                          }}
                        >
                          {trans(
                            "Too many login attempts. Please try again in",
                          )}{" "}
                          <strong>{lockoutDisplay}</strong>
                        </div>
                      )}
                      <Input
                        title={trans("Email")}
                        type="email"
                        placeholder={trans("Enter Your Email")}
                        onBlur={register.handleBlur}
                        onChange={register.handleChange}
                        errors={register.errors.email}
                        touched={register.touched.email}
                        formstyle="login-input"
                        id="email"
                        name="email"
                        disabled={isLockedOut}
                      />
                      <Input
                        title={trans("Password")}
                        type="password"
                        placeholder={trans("Enter Your Password")}
                        onBlur={register.handleBlur}
                        onChange={register.handleChange}
                        errors={register.errors.password}
                        touched={register.touched.password}
                        formstyle="login-input"
                        id="password"
                        name="password"
                        disabled={isLockedOut}
                      />
                      <div
                        className={`${
                          lang === "ar" ? "text-start" : "text-end"
                        } mt-8`}
                      >
                        <div
                          onClick={switchLanguage}
                          className="text-white text-decoration-none hover-yellow"
                          style={{ cursor: "pointer" }}
                        >
                          {lang === "ar" ? "English" : "العربية"}
                        </div>
                      </div>
                      <div className="text-center">
                        <Button
                          text={
                            isLockedOut
                              ? trans("Try again in") + " " + lockoutDisplay
                              : trans("Login")
                          }
                          htmlType="submit"
                          type="primary"
                          className={"login-button"}
                          disabled={isLockedOut}
                        />
                        {submitAttempt && !register.isValid && (
                          <div
                            className={`validation login-input ${
                              lang === "en" ? "text-start" : "text-end"
                            }`}
                          >
                            {trans("Please Check Required Inputs")}
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="welcome-footer">
                <p dir="ltr">
                  © 2025 Soft Sprint. {trans("All rights reserved")}.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
